import type { GeofenceState } from "./types";

/**
 * Geofence mock (TASK-004): the real 3 m service fence is not yet wired to a
 * backend geofence service, so S1 evaluates against a CONFIGURABLE service
 * centre + radius. Override via env:
 *   VITE_SERVICE_CENTER_LAT, VITE_SERVICE_CENTER_LNG, VITE_SERVICE_FENCE_RADIUS_M
 * Defaults anchor on the Kinondoni service area used in the approved designs.
 */

function numEnv(name: string, fallback: number): number {
  const raw = import.meta.env[name];
  const parsed = raw === undefined ? NaN : Number(raw);
  return Number.isFinite(parsed) ? parsed : fallback;
}

export const SERVICE_CENTER = {
  lat: numEnv("VITE_SERVICE_CENTER_LAT", -6.7712),
  lng: numEnv("VITE_SERVICE_CENTER_LNG", 39.2399),
  radiusM: numEnv("VITE_SERVICE_FENCE_RADIUS_M", 250),
  label: "Kinondoni",
} as const;

/** Haversine distance in metres between two WGS84 points. */
export function haversineM(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const R = 6_371_000;
  const toRad = (d: number) => (d * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(a));
}

export interface GeofenceResult {
  state: GeofenceState;
  /** Distance from the service-centre fence edge, metres (negative = inside). */
  distanceFromFenceM: number;
  distanceFromCenterM: number;
}

/** Evaluate a captured coordinate against the configured service fence. */
export function evaluateGeofence(lat: number, lng: number): GeofenceResult {
  const distanceFromCenterM = haversineM(lat, lng, SERVICE_CENTER.lat, SERVICE_CENTER.lng);
  const distanceFromFenceM = distanceFromCenterM - SERVICE_CENTER.radiusM;
  return {
    state: distanceFromCenterM <= SERVICE_CENTER.radiusM ? "in_range" : "out_of_range",
    distanceFromFenceM,
    distanceFromCenterM,
  };
}

export function formatCoord(lat: number, lng: number): string {
  return `${lat.toFixed(4)}, ${lng.toFixed(4)}`;
}
